// @flow

import type {
  Decoded,
  Decoder,
  ArrayDecoder,
} from '../types'

export function numberDecoder (maybeNr: any): Decoded<number> {
  const n = Number(maybeNr || 'NaN')

  if (isNaN(n)) return new TypeError(`Error: ${maybeNr} is can not be decoded into a number.`)
  return n
}

export function booleanDecoder (maybeBool: any): Decoded<boolean> {
  return !!maybeBool
}

export function stringDecoder (maybeStr: any): Decoded<string> {
  if (maybeStr == null) return ''
  return `${maybeStr}`
}

export function nullableDecoder<T> (decoder: Decoder<T>): Decoder<?T> {
  return function (maybeT: any): Decoded<?T> {
    if (maybeT == null) return null
    const result: Decoded<T> = decoder(maybeT)

    if (result instanceof TypeError) {
      return null
    }

    return result
  }
}

export function arrayDecoder<T> (decoder: Decoder<T>): ArrayDecoder<T> {
  return function decodeArray (value: any): Array<T> {
    if (value != null) {
      if (Array.isArray(value)) {
        const result = value.map(decoder)
        return result.filter((r: Decoded<T>): boolean => r instanceof TypeError)
      } else {
        return decodeArray([value])
      }
    }

    return []
  }
}

type ExtractReturnType = <V>(() => V) => V

type PropDecoder<A> = {[key: string]: (value: any) => A}
type DecodedObject<A, O: PropDecoder<A>> = $ObjMap<O, ExtractReturnType>
type ObjectDecoder<A, O: PropDecoder<A>> = (value: any) => DecodedObject<A, O>

export function recordDecoder<A, O: {[key: string]: (value: any) => A}>(o: O): ObjectDecoder<A, O> {
  return function decodeRecord (maybeValue: any): DecodedObject<A, O> {
    const value: Decoded<Object> = objectDecoder(maybeValue)
    const object: Object = (map(value) || {})

    return Object.keys(o).reduce((acc: DecodedObject<A, O>, k: string): DecodedObject<A, O> => {
      const decoder = o[k]
      return Object.assign(acc, { [k]: decoder(object[k]) })
    }, {})
  }
}

const nullableNumberDecoder = nullableDecoder(numberDecoder)

export function primaryKeyDecoder (value: any): Decoded<?number> {
  const pk: Decoded<?number> = nullableNumberDecoder(value)

  if (pk != null && typeof pk === 'number' && pk >= 1) return pk

  return null
}

export function composeFloatDecoder (maybePrecision: ?number): Decoder<number> {
  const precision = maybePrecision != null
    ? maybePrecision
    : 15

  return function floatDecoder (value: any): number {
    if (value != null) {
      const n: number = Number(value)

      if (isNaN(n)) {
        return 0
      }

      // $FlowFixMe
      return Number(Math.round(n + 'e' + precision) + 'e-' + precision) || 0
    }

    return 0
  }
}

export function objectDecoder (value: any): Decoded<Object> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value
  }

  return new TypeError(`Unable to decode '${value}' to object.`)
}

export function composeSuccessDecoder<T> (defaultTo: T, decoder: Decoder<T>): Decoder<T> {
  return function successDecoder (value: any): T {
    const result: Decoded<T> = decoder(value)

    if (result instanceof TypeError) {
      return defaultTo
    }

    return map(result) || defaultTo
  }
}

export function map<T> (decoded: Decoded<T>): ?T {
  if (decoded instanceof TypeError) {
    return null
  }

  return decoded
}

export function modelDecoder<A, O: PropDecoder<A>> (decoder: ObjectDecoder<A, O>): (value: any) => ?Object {
  return function (value: any): ?Object {
    const result: DecodedObject<A, O> = decoder(value)
    const failed: boolean = Object.keys(result).some(function (k: string): boolean {
      return result[k] instanceof TypeError
    })

    return failed
      ? null
      : Object.keys(result).reduce(function (acc: DecodedObject<A, O>, k: string): DecodedObject<A, O> {
        acc[k] = map(result[k])
        return acc
      }, {})
  }
}

export default {
  string: stringDecoder,
  number: numberDecoder,
  boolean: booleanDecoder,
  maybe: nullableDecoder,
  record: recordDecoder,
  object: objectDecoder,
  array: arrayDecoder,
  pk: primaryKeyDecoder,
  float: composeFloatDecoder(15),
  floatAt: composeFloatDecoder,
  success: composeSuccessDecoder,
  model: modelDecoder,
  map: map,
}
